import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, FileText, Globe2, HeartHandshake } from 'lucide-react';

const Resources: React.FC = () => {
  const { t } = useTranslation();

  const resourceGroups = [
    {
      icon: BookOpen,
      title: t('resources.language.title', 'Language Resources'),
      japanese: '日本語を学ぶ',
      items: ['Japanese Language Classes', 'Study Materials', 'Practice Groups', 'Online Learning Tools'],
      accent: 'bg-[#d95f75]',
    },
    {
      icon: Globe2,
      title: t('resources.culture.title', 'Cultural Resources'),
      japanese: '文化を知る',
      items: ['Traditional Arts', 'Cultural Events', 'Japanese Festivals', 'Cultural Exchange Programs'],
      accent: 'bg-[#2457a7]',
    },
    {
      icon: HeartHandshake,
      title: t('resources.support.title', 'Community Support'),
      japanese: '地域とつながる',
      items: ['Newcomer Support', 'Business Directory', 'Healthcare Information', 'Education Resources'],
      accent: 'bg-[#b28a24]',
    },
  ];

  return (
    <section className="page-section">
      <div className="page-container">
        <div className="page-intro">
          <p className="eyebrow">Learn and connect</p>
          <h1 className="page-title">{t('resources.title', 'Resources')}</h1>
          <p className="page-description">A practical starting point for language learning, cultural discovery, and life in the Jacksonville community.</p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {resourceGroups.map(({ icon: Icon, title, japanese, items, accent }) => (
            <article key={title} className="surface overflow-hidden">
              <div className={`h-1.5 ${accent}`} />
              <div className="p-6 sm:p-7">
                <div className="flex h-11 w-11 items-center justify-center bg-[#eef2f7] text-[#2457a7]">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="mt-6 text-xl font-extrabold text-[#172033]">{title}</h2>
                <p className="font-japanese mt-1 text-sm text-[#d95f75]">{japanese}</p>
                <ul className="mt-6 divide-y divide-[#edf0f3] text-sm text-[#667085]">
                  {items.map((item) => <li key={item} className="py-3">{item}</li>)}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12">
          <div className="mb-6 flex items-center gap-3">
            <FileText className="h-6 w-6 text-[#d95f75]" />
            <h2 className="section-title">{t('resources.documents.title', 'Important Documents')}</h2>
          </div>
          <div className="surface grid gap-px overflow-hidden bg-[#e3e7ec] md:grid-cols-2">
            {[
              ['Forms', 'Membership Application Form', 'Event Registration Form', 'Volunteer Application'],
              ['Guides', 'New Member Guide', 'Community Guidelines', 'Event Planning Guide'],
            ].map(([title, ...items]) => (
              <div key={title} className="bg-white p-6 sm:p-8">
                <h3 className="text-xs font-bold uppercase text-[#2457a7]">{title}</h3>
                <div className="mt-4 space-y-3">
                  {items.map((item) => <p key={item} className="text-sm font-semibold text-[#4b5565]">{item}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resources;
